import type { AuthUserDto } from '../generated/contracts';
import { ALL_MODES, ALWAYS_BINDINGS, MODE_BINDINGS, type ControlBinding } from '../input/ControlBindings';
import type { Mode } from '../possession/ToolMode';
import { PortalShell } from './portal/shell';

/**
 * The portal's controls page (task brief: "the owner wants somewhere to read the controls
 * before entering a room, so an unguided first-timer can prepare" — Ash may drop in and test
 * with nobody there to walk him through it).
 *
 * **Every binding list on this page is rendered straight off `ControlBindings.ts`** — the
 * same table the in-headset look-at panel (`ControlHintsPanel`/`ControlHintsController`)
 * already reads, shipped for that reason precisely so nothing here can drift: a binding
 * added, removed or reworded there needs no matching edit here, and a binding *removed*
 * there cannot linger in this page's prose the way recentre once lingered in a document
 * after being deleted from the code (`plan/grid.md`).
 *
 * **Prose here may say what you can do; it must never name a specific control.** A
 * hand-written "press X to..." is a second copy of a fact this file's own binding table
 * already owns, and second copies rot — see this file's own module comment above and
 * `plan/grid.md`'s account of exactly that happening. The prose below is ordering and
 * orientation only ("here is roughly what to try first"); every concrete gesture/action pair
 * comes from `bindingRows()`, not from a sentence someone typed by hand.
 *
 * **Structured by what someone does first**, not by control or by `Mode` — CLAUDE.md task
 * brief: get in, move, make something, draw, link two things together, talk to Gemma, and —
 * last, deliberately — look down at your controller once you forget. That last section is
 * prose only and names no binding at all, on purpose: it hands off to the in-headset panel,
 * so this page never has to be a complete reference.
 *
 * **No binding can go silently missing.** Every entry in `ALWAYS_BINDINGS` and every mode in
 * `MODE_BINDINGS` is assigned to a section below by an explicit key, but anything this file's
 * own assignment does not recognise — a control added to `ALWAYS_BINDINGS`, or an entirely
 * new `Mode` — falls through to a generic "Other controls"/"Other tools" list rather than
 * disappearing, so a future binding is always rendered *somewhere* even before this page's
 * own hand-picked sections are updated to describe it properly. `PortalControls.test.ts`
 * checks this directly: every binding the table holds must appear in the rendered page text.
 * Link mode landed in the same shape once already (`fca7ade`) and sat in that fallback bucket
 * until this pass gave it `LINK_MODES`/`linkBindings()` and its own section, below — the exact
 * gap `plan/ui-improvements.md` item 6 flagged in advance: "a new Link mode would land in that
 * generic fallback automatically... reading correctly as its own proper section... needs the
 * same hand-editing."
 */
export class PortalControls {
  private readonly root: HTMLElement;

  private readonly shell: PortalShell;

  constructor(host: HTMLElement, account: AuthUserDto, private readonly onBack: () => void) {
    // The same shell every portal page wears — a persistent rail with the surface's three
    // destinations, the account, and the way out. The page this replaces built its own top bar
    // per route, which is how "Controls" and "Exports" ended up rendered as sign-out buttons:
    // .portal-logout was the only button class that existed.
    this.shell = new PortalShell(host, account, '/portal/controls', {
      go: (route) => {
        if (route === '/portal') {
          this.onBack();
        }
      },
    });

    this.root = this.shell.main;
    this.root.innerHTML = `
      <header class="page-head">
        <h1>Controls</h1>
        <p>What to try first. The same list is on your controller in the room.</p>
      </header>

        <section class="card section card-padded" aria-labelledby="controls-get-in-heading">
          <h2 id="controls-get-in-heading">1. Get in</h2>
          <p>
            Sign in here in the portal first — everything else, including the room itself,
            needs a signed-in session. From the entry screen, the headset only has one real job
            left: press Enter VR. Do that as your very first action in the headset, not after
            poking around — a browser only grants a VR session from a fresh press, and anything
            slow has already happened before you see the button.
          </p>
        </section>

        <section class="card section card-padded" aria-labelledby="controls-move-heading">
          <h2 id="controls-move-heading">2. Move</h2>
          <p>
            Your off hand — the one that is not doing the pointing and grabbing — is also how
            you get around the room and turn to face a new direction.
          </p>
          <div data-section="move"></div>
        </section>

        <section class="card section card-padded" aria-labelledby="controls-make-heading">
          <h2 id="controls-make-heading">3. Make something</h2>
          <p>
            The off hand opens a tray of shapes; aim and confirm to place one. There is also a
            way to sweep a real object into a shape rather than picking one from the tray, if
            you would rather trace something in front of you than choose from a list.
          </p>
          <div data-section="make"></div>
        </section>

        <section class="card section card-padded" aria-labelledby="controls-draw-heading">
          <h2 id="controls-draw-heading">4. Draw</h2>
          <p>
            Everything so far has been Build, the mode you start in. To reach any other mode —
            Draw, Scan, or Link — hold your dominant grip to bring up a ring of the modes on
            offer, then flick the stick toward the one you want. Draw is the one for the
            whiteboard on the wall: touch the tip to the board, or draw on it from a short
            distance away. With the grip released, the same stick picks a marker width or the
            eraser, and the colour to draw with.
          </p>
          <div data-section="draw"></div>
        </section>

        <section class="card section card-padded" aria-labelledby="controls-link-heading">
          <h2 id="controls-link-heading">5. Link two things together</h2>
          <p>
            Switch to Link mode the same way. Aim at one shape, pull the trigger, aim at
            another, and let go — that draws the connection between them. Landing the identical
            drag on a pair that is already connected restyles it instead of making a second one:
            the stick picks the line style and arrow direction beforehand, cycling the style all
            the way round to "remove" takes the connection away, and dragging from the other end
            reverses its direction.
          </p>
          <div data-section="link"></div>
        </section>

        <section class="card section card-padded" aria-labelledby="controls-talk-heading">
          <h2 id="controls-talk-heading">6. Talk to Gemma</h2>
          <p>
            Your off hand talks to the room's own assistant. <strong>The microphone is live to
            everyone else in the room by default</strong> — worth knowing before you speak, the
            same as it would be on any shared call.
          </p>
          <div data-section="talk"></div>
        </section>

        <div data-section="other"></div>

        <section class="card section card-padded" aria-labelledby="controls-forget-heading">
          <h2 id="controls-forget-heading">7. When you forget</h2>
          <p>
            Look down at your own controller. The same reference this page draws from is right
            there in the room, always current — that is deliberately the whole safety net, so
            this page never has to be a complete manual, only a running start.
          </p>
        </section>
    `;

    this.renderSection('move', moveBindings());
    this.renderSection('make', makeBindings());
    this.renderSection('draw', drawBindings());
    this.renderSection('link', linkBindings());
    this.renderSection('talk', talkBindings());

    const other = otherBindings();
    if (other.length > 0) {
      this.renderOtherSection(other);
    }
  }

  dispose(): void {
    this.shell.dispose();
  }

  private renderSection(name: string, bindings: readonly ControlBinding[]): void {
    const mount = this.query(`[data-section="${name}"]`);
    mount.appendChild(bindingList(bindings));
  }

  /** A catch-all card for any binding this page's own hand-picked sections above do not yet
   *  recognise — see this class's own module comment, "No binding can go silently missing."
   *  Never rendered at all when there is nothing left uncategorised, which is the normal
   *  case today.
   *
   *  Replaces the `[data-section="other"]` placeholder in place, rather than appending a new
   *  `<section>` to `.dashboard-main` — appending would add it as the *last* child regardless
   *  of where the placeholder sits in the markup, which pushed the hard-coded "7. When you
   *  forget" section (deliberately last, per this class's own module doc: "structured by what
   *  someone does first ... look down at your controller once you forget") out of last place
   *  the first time this branch ever had anything to render. */
  private renderOtherSection(bindings: readonly ControlBinding[]): void {
    const section = document.createElement('section');
    section.className = 'dashboard-card';
    section.setAttribute('aria-labelledby', 'controls-other-heading');

    const heading = document.createElement('h2');
    heading.id = 'controls-other-heading';
    heading.textContent = 'Other controls';

    const note = document.createElement('p');
    note.textContent =
      "Not yet sorted into a section above — everything still listed here, regardless.";

    section.append(heading, note, bindingList(bindings));
    this.query('[data-section="other"]').replaceWith(section);
  }

  private query<T extends HTMLElement = HTMLElement>(selector: string): T {
    const found = this.root.querySelector<T>(selector);

    if (!found) {
      throw new Error(`PortalControls markup is missing ${selector}.`);
    }

    return found;
  }
}

// --- binding assignment ---------------------------------------------------------------

/** `ALWAYS_BINDINGS` entries this page places under "Move" — see the module comment's own
 *  "structured by what someone does first" reasoning for why this key set, not a mode, is
 *  what earns a place here. */
const MOVE_ALWAYS_KEYS = new Set(['off:stick']);

const MAKE_ALWAYS_KEYS = new Set(['off:near', 'off:far']);

/** The two `ALWAYS_BINDINGS` entries for the mode-switch gesture itself — "hold to show the
 *  mode ring" and "grip+stick switches mode" — placed under "Draw" rather than a section of
 *  their own: Draw is the first mode a newcomer needs to reach that is not the one they start
 *  in (Build), so it is also the first natural place to explain how to get there at all. Link
 *  reuses the identical gesture (`linkBindings()`, below) without repeating these two keys —
 *  the mode switch is described once, not once per destination mode. */
const DRAW_ALWAYS_KEYS = new Set(['dominant:near', 'dominant:stick']);

const TALK_ALWAYS_KEYS = new Set(['off:talkButton', 'off:micToggleButton']);

/** `Mode`s rendered under "Make something" — scanning builds a shape the same way picking one
 *  from the tray does, just from a swept real-space selection instead of a palette choice. */
const MAKE_MODES: readonly Mode[] = ['build', 'scan'];

const DRAW_MODES: readonly Mode[] = ['draw'];

const LINK_MODES: readonly Mode[] = ['link'];

function keyOf(binding: ControlBinding): string {
  return `${binding.hand}:${binding.control}`;
}

function moveBindings(): readonly ControlBinding[] {
  return ALWAYS_BINDINGS.filter((b) => MOVE_ALWAYS_KEYS.has(keyOf(b)));
}

function makeBindings(): readonly ControlBinding[] {
  return [
    ...ALWAYS_BINDINGS.filter((b) => MAKE_ALWAYS_KEYS.has(keyOf(b))),
    ...MAKE_MODES.flatMap((mode) => MODE_BINDINGS[mode]),
  ];
}

function drawBindings(): readonly ControlBinding[] {
  return [
    ...ALWAYS_BINDINGS.filter((b) => DRAW_ALWAYS_KEYS.has(keyOf(b))),
    ...DRAW_MODES.flatMap((mode) => MODE_BINDINGS[mode]),
  ];
}

function linkBindings(): readonly ControlBinding[] {
  return LINK_MODES.flatMap((mode) => MODE_BINDINGS[mode]);
}

function talkBindings(): readonly ControlBinding[] {
  return ALWAYS_BINDINGS.filter((b) => TALK_ALWAYS_KEYS.has(keyOf(b)));
}

/**
 * Every binding not claimed by one of the five sections above — always empty today (every
 * `ALWAYS_BINDINGS` entry and every `Mode` in `ALL_MODES` is accounted for by the key sets
 * above), and that is the point: this function, not a hand count, is what keeps it that way
 * as `ControlBindings.ts` changes. See the class's own "No binding can go silently missing."
 */
function otherBindings(): readonly ControlBinding[] {
  const claimedAlwaysKeys = new Set([
    ...MOVE_ALWAYS_KEYS,
    ...MAKE_ALWAYS_KEYS,
    ...DRAW_ALWAYS_KEYS,
    ...TALK_ALWAYS_KEYS,
  ]);
  const claimedModes = new Set<Mode>([...MAKE_MODES, ...DRAW_MODES, ...LINK_MODES]);

  const leftoverAlways = ALWAYS_BINDINGS.filter((b) => !claimedAlwaysKeys.has(keyOf(b)));
  const leftoverModes = ALL_MODES.filter((mode) => !claimedModes.has(mode)).flatMap(
    (mode) => MODE_BINDINGS[mode],
  );

  return [...leftoverAlways, ...leftoverModes];
}

function bindingList(bindings: readonly ControlBinding[]): HTMLElement {
  const list = document.createElement('ul');
  list.className = 'controls-binding-list';

  for (const binding of bindings) {
    const item = document.createElement('li');
    item.className = 'controls-binding';

    const hand = document.createElement('span');
    hand.className = 'controls-hand-badge';
    hand.textContent = binding.hand === 'dominant' ? 'Dominant hand' : 'Off hand';

    const gesture = document.createElement('strong');
    gesture.textContent = binding.gesture;

    const description = document.createElement('span');
    description.textContent = ` — ${binding.description}`;

    item.append(hand, gesture, description);
    list.appendChild(item);
  }

  return list;
}
